import { Fragment } from 'react'

/**
 * Matches http(s) URLs inside plain text. Trailing punctuation that's more
 * likely to be sentence punctuation than part of the URL (.,;:!?) and a
 * closing paren/bracket/quote with no matching opener earlier in the match
 * are trimmed off the match and left in the surrounding text.
 */
const URL_PATTERN = /https?:\/\/[^\s<>]+/g

const TRAILING_PUNCTUATION = /[.,;:!?]+$/
const CLOSING_WRAPPERS: Record<string, string> = {
  ')': '(',
  ']': '[',
  '}': '{',
  '"': '"',
  "'": "'",
}

function countOccurrences(text: string, char: string): number {
  return text.split(char).length - 1
}

/**
 * Whether the wrapper char trailing `text` (e.g. a closing paren) has no
 * matching opener earlier in `text`, and so isn't really part of the URL.
 * Counts balance rather than mere presence, so `Cape_Cod_(town)` (matched
 * pair) is left alone while the outer `)` in `(see .../Cape_Cod_(town))`
 * (no matching `(` of its own) is trimmed. Quote characters use the same
 * char as opener and closer, so an odd count means the trailing one is
 * unmatched.
 */
function isTrailingWrapperUnmatched(text: string, closer: string, opener: string): boolean {
  if (opener === closer) {
    return countOccurrences(text, closer) % 2 === 1
  }
  return countOccurrences(text, closer) > countOccurrences(text, opener)
}

/**
 * Strips punctuation/closing-wrapper characters that shouldn't be part of
 * the URL itself. Punctuation and wrappers are trimmed together, one
 * character at a time, since either can expose the other underneath -
 * e.g. a trailing `."` needs the quote trimmed before the now-exposed `.`
 * is recognized as sentence punctuation rather than part of the URL.
 */
function trimTrailing(url: string): string {
  let trimmed = url
  let changed = true
  while (changed && trimmed.length > 0) {
    changed = false

    const punctuationMatch = trimmed.match(TRAILING_PUNCTUATION)
    if (punctuationMatch) {
      trimmed = trimmed.slice(0, trimmed.length - punctuationMatch[0].length)
      changed = true
      continue
    }

    const lastChar = trimmed[trimmed.length - 1]
    const opener = CLOSING_WRAPPERS[lastChar]
    if (opener && isTrailingWrapperUnmatched(trimmed, lastChar, opener)) {
      trimmed = trimmed.slice(0, -1)
      changed = true
    }
  }
  return trimmed
}

/**
 * Renders plain text with any http(s) URLs turned into links that open in a
 * new tab. Everything else is rendered verbatim, so the caller's own
 * whitespace handling (e.g. `whitespace-pre-wrap`) still applies.
 */
export function LinkifiedText({ text }: { text: string }) {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0

  for (const match of text.matchAll(URL_PATTERN)) {
    const rawUrl = match[0]
    const url = trimTrailing(rawUrl)
    const matchStart = match.index ?? 0
    const matchEnd = matchStart + url.length

    if (matchStart > lastIndex) {
      parts.push(<Fragment key={key++}>{text.slice(lastIndex, matchStart)}</Fragment>)
    }

    parts.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:opacity-80"
        onClick={event => event.stopPropagation()}
      >
        {url}
      </a>
    )

    lastIndex = matchEnd
  }

  if (lastIndex < text.length) {
    parts.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>)
  }

  return <>{parts}</>
}
